/**
 * `/keep?slug=…&agent_id=…&name=…` — the web half of the "put this genie in
 * your app" share link.
 *
 * helpgenie.ai declares `/keep` in its apple-app-site-association, and the
 * native app owns the matching `/keep` route, so on a phone WITH the app this
 * page is never rendered: iOS/Android hand the URL straight to the app, which
 * writes the genie into My Genies and opens the conversation.
 *
 * This page is the fallback for everyone else — no app, desktop, or a browser
 * that swallowed the universal link. Previously that case 404'd, which is what
 * made shared genies feel broken outside the app.
 *
 *  - Desktop: no app exists, so go straight to the web conversation.
 *  - Phone:   offer the store as the primary action, with "talk in your
 *             browser" always available underneath. Nobody dead-ends.
 *
 * Deliberately mirrors SmartActivationLink (`/get/:packId`) so the two
 * interstitials read as one family.
 */
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { useAgentPageByUrlNameFull } from "@/hooks/agent/useAgentPage";
import { buildGenieTalkUrl } from "@/utils/agent/genieShareLinks";
import { getMobilePlatform } from "@/utils/device";
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  APP_STORE_BADGE_SRC,
  GOOGLE_PLAY_BADGE_SRC,
} from "@/constants/appStore";

const FALLBACK_PRIMARY = "#4E9CFF";
const FALLBACK_SECONDARY = "#FF4DB8";

const readBrandColor = (
  branding: unknown,
  key: "primaryColor" | "secondaryColor",
): string | null => {
  if (!branding || typeof branding !== "object") return null;
  const value = Reflect.get(branding, key);
  return typeof value === "string" && value.trim() ? value : null;
};

export const KeepGenieLink = () => {
  const [searchParams] = useSearchParams();
  const slug = searchParams.get("slug")?.trim() || "";
  const nameParam = searchParams.get("name")?.trim().slice(0, 80) || "";
  const platform = useMemo(() => getMobilePlatform(), []);

  const { data, isLoading } = useAgentPageByUrlNameFull(slug || null);
  const agent = data?.status === "ok" ? data.agent : null;

  // The `name` query param renders instantly; the fetched name wins once it
  // lands so a renamed genie never shows a stale label.
  const name = agent?.name || nameParam || "this Genie";
  const primary = readBrandColor(agent?.branding, "primaryColor") || FALLBACK_PRIMARY;
  const secondary =
    readBrandColor(agent?.branding, "secondaryColor") || FALLBACK_SECONDARY;

  const talkUrl = buildGenieTalkUrl(slug, { receiveIntent: true });
  const storeUrl = platform === "android" ? GOOGLE_PLAY_URL : APP_STORE_URL;
  const storeBadgeSrc =
    platform === "android" ? GOOGLE_PLAY_BADGE_SRC : APP_STORE_BADGE_SRC;
  const storeBadgeAlt =
    platform === "android"
      ? "Get it on Google Play"
      : "Download on the App Store";

  const isPrivate = data?.status === "private";
  const isMissing = !slug || (!isLoading && (data === null || data?.status === "not_found"));

  // Desktop has no app to hand off to — skip the interstitial entirely.
  useEffect(() => {
    if (platform === "other" && talkUrl && !isPrivate && !isMissing && !isLoading) {
      window.location.replace(talkUrl);
    }
  }, [platform, talkUrl, isPrivate, isMissing, isLoading]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center text-center px-6"
      style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
    >
      <Helmet>
        <meta name="robots" content="noindex" />
        <title>{`Keep ${name} in your Help Genie app`}</title>
      </Helmet>

      {isMissing ? (
        <>
          <h1 className="text-white text-2xl font-bold">This link isn&rsquo;t available</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            The Genie you&rsquo;re looking for may have moved or is no longer active.
          </p>
          <a
            href="/"
            className="mt-6 inline-flex items-center px-5 py-2.5 rounded-xl bg-white font-semibold"
            style={{ color: primary }}
          >
            Go to Help Genie
          </a>
        </>
      ) : isPrivate ? (
        <>
          <h1 className="text-white text-2xl font-bold">{name} is private</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            Ask whoever shared this to send you an invite, and it&rsquo;ll drop straight
            into your Help Genie app.
          </p>
          {talkUrl && (
            <a
              href={talkUrl}
              className="mt-6 inline-flex items-center px-5 py-2.5 rounded-xl bg-white font-semibold"
              style={{ color: primary }}
            >
              Request access
            </a>
          )}
        </>
      ) : platform === "other" ? (
        <>
          <Loader2 className="w-10 h-10 text-white animate-spin mb-5" />
          <h1 className="text-white text-xl font-semibold">Opening {name}&hellip;</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            Taking you to the best place to start the conversation.
          </p>
        </>
      ) : (
        <>
          <h1 className="text-white text-2xl font-semibold">Keep {name} in your app</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            Install Help Genie and {name} lands in My Genies, ready whenever you need
            it. Already have the app? Tap the link again and it&rsquo;ll open there.
          </p>
          <div className="mt-8 flex flex-col items-center gap-4">
            <a
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={storeBadgeAlt}
              className="inline-flex rounded-xl bg-white px-5 py-4 shadow-lg hover:scale-105 transition-transform"
            >
              <img src={storeBadgeSrc} alt={storeBadgeAlt} className="h-11" />
            </a>
            {talkUrl && (
              <a href={talkUrl} className="text-white underline text-sm font-medium">
                Talk to {name} in your browser instead
              </a>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default KeepGenieLink;
